import { BRAVEMOVE_PALETTES, type PaletteKey } from '@bravemove/design-tokens';
import type { VenueListItem } from './useMapClusters';

export interface HoverCardColors {
  readonly ink: string;
  readonly ink2: string;
  readonly accent: string;
}

/**
 * Google draws the InfoWindow white in every theme and we don't restyle it, so
 * its text always comes from the light sibling of the active palette. Dark ink
 * on that card is about 1.2:1 (R2-12).
 */
export function hoverCardColors(palette: PaletteKey): HoverCardColors {
  const light = BRAVEMOVE_PALETTES[palette.replace('_dark', '_light') as PaletteKey];
  return { ink: light.ink, ink2: light.ink2, accent: light.accent };
}

/** Minimal HTML-escape for venue-supplied strings injected into the hover card. */
function escHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;',
  );
}

/** "table-tennis" → "Table Tennis" for the hover sport chips. */
function formatSport(s: string): string {
  return s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Build the InfoWindow HTML shown when hovering a single-venue pin: the venue
 * name, its status + tier, and a chip per sport. Colours come from
 * `hoverCardColors` so there are no raw literals; the chip background is the
 * accent at ~14% via an 8-digit-hex alpha suffix.
 */
export function buildVenueHoverHtml(venue: VenueListItem, colors: HoverCardColors): string {
  const { ink, ink2, accent } = colors;
  const name = escHtml(String(venue.name ?? 'Venue'));
  const status = escHtml(String((venue as Record<string, unknown>).status ?? ''));
  const tier = String(venue.tier ?? '').replace('tier_', 'Tier ');
  const meta = [status, tier].filter(Boolean).join(' · ');
  const chips = venue.sports
    .map(
      (s) =>
        `<span style="display:inline-block;background:${accent}24;color:${accent};border-radius:999px;padding:1px 8px;font-size:10.5px;font-weight:600;">${escHtml(
          formatSport(s.sportName),
        )}</span>`,
    )
    .join('');
  return (
    `<div style="font-family:system-ui,-apple-system,sans-serif;min-width:150px;max-width:230px;padding:1px 2px;">` +
    `<div style="font-weight:700;font-size:13px;color:${ink};line-height:1.25;">${name}</div>` +
    (meta
      ? `<div style="font-size:10.5px;color:${ink2};margin-top:2px;text-transform:capitalize;">${meta}</div>`
      : '') +
    (chips
      ? `<div style="margin-top:6px;display:flex;flex-wrap:wrap;gap:4px;">${chips}</div>`
      : '') +
    `</div>`
  );
}
